import { useRef, useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Play, Volume2, VolumeX, Maximize2, MessageCircle, ArrowRight } from "lucide-react";

const VIDEO_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_territoria-wiki/artifacts/8r5yzw00_website.mp4";
const PLAYBACK_RATE = 2.0;
const DISCORD_URL = "https://discord.gg/territoria";

export default function DevShowcase() {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true); // autoplay muted

  // Force 2x playback and keep it locked even after loop/seek/rate resets
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const applyRate = () => { if (v.playbackRate !== PLAYBACK_RATE) v.playbackRate = PLAYBACK_RATE; };
    applyRate();
    v.addEventListener("loadedmetadata", applyRate);
    v.addEventListener("play", applyRate);
    v.addEventListener("ratechange", applyRate);
    v.addEventListener("seeked", applyRate);
    return () => {
      v.removeEventListener("loadedmetadata", applyRate);
      v.removeEventListener("play", applyRate);
      v.removeEventListener("ratechange", applyRate);
      v.removeEventListener("seeked", applyRate);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const goFullscreen = (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <section className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" data-testid="dev-showcase">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-6 md:gap-10 items-center">
        {/* COPY */}
        <div className="order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--t-blue)] mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--t-red)] pulse-glow" />
            <span className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-cyan)] uppercase">{t("home.devFeedTag")}</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl uppercase leading-[1.05] mb-4">
            <span className="block text-[var(--t-text)]">{t("home.devFeedTitle1")}</span>
            <span className="block bg-gradient-to-r from-[var(--t-blue-glow)] via-[var(--t-cyan)] to-[var(--t-blue)] bg-clip-text text-transparent">{t("home.devFeedTitle2")}</span>
          </h2>
          <p className="text-sm md:text-base text-[var(--t-text-dim)] leading-relaxed mb-5">
            <Trans
              i18nKey="home.devFeedBlurb"
              components={[
                <span key="days" className="font-mono text-[var(--t-cyan)]" />,
                <span key="players" className="font-mono text-[var(--t-cyan)]" />,
              ]}
            />
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-sm">
            <DevStat label={t("home.devFeedVersion")} value="0.0.007" />
            <DevStat label={t("home.devFeedDays")} value="32" />
            <DevStat label={t("home.devFeedStatus")} value={t("home.devFeedStatusValue")} />
          </div>

          <div className="mt-6 md:mt-7">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              className="relative inline-flex items-center justify-center gap-2 px-5 py-3 md:py-3.5 font-display tracking-[0.14em] uppercase text-sm md:text-base text-white border transition group"
              style={{
                background: "linear-gradient(180deg, #5865F2 0%, #404eed 100%)",
                borderColor: "#7c87ff",
                clipPath: "polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)",
                boxShadow: "0 0 0 1px rgba(124,135,255,0.5), inset 0 1px 0 rgba(255,255,255,0.18), 0 10px 32px rgba(88,101,242,0.45)",
              }}
              data-testid="dev-showcase-join-btn"
            >
              <MessageCircle className="w-5 h-5" />
              {t("home.devFeedJoinBtn")}
              <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
            </a>
            <div className="mt-2 font-mono text-[10px] md:text-[11px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">
              {t("home.devFeedJoinNote")}
            </div>
          </div>
        </div>

        {/* VIDEO */}
        <div className="order-1 lg:order-2 relative group" onClick={togglePlay}>
          <div className="absolute -inset-2 md:-inset-3 bg-[radial-gradient(circle,_rgba(58,160,255,0.25)_0%,_transparent_70%)] blur-2xl pointer-events-none" />
          <div className="relative panel panel-glow corner-frame overflow-hidden cursor-pointer">
            {/* Top frame */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2 bg-[#050912]/90 border-b border-[var(--t-border)]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--t-red)] pulse-glow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-cyan)] uppercase">{t("home.devFeedRec")}</span>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono text-[var(--t-text-muted)] tracking-[0.15em] uppercase">
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[var(--t-green)]" /> {t("home.devFeedLiveSignal")}</span>
              </div>
            </div>

            <div className="relative bg-black aspect-video">
              <video
                ref={videoRef}
                src={VIDEO_URL}
                className="w-full h-full object-contain"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                data-testid="showcase-video"
              />
              {/* Scan-line overlay */}
              <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: "repeating-linear-gradient(180deg, transparent 0px, transparent 3px, rgba(58,160,255,0.07) 3px, rgba(58,160,255,0.07) 4px)" }} />
              <div className="scan-line pointer-events-none" />

              {/* Play overlay (when paused) */}
              {!playing && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#050912]/40 backdrop-blur-[2px]">
                  <div className="w-20 h-20 rounded-full border-2 border-[var(--t-blue)] inline-flex items-center justify-center bg-[var(--t-bg)]/80 group-hover:scale-110 transition" style={{ boxShadow: "0 0 40px rgba(58,160,255,0.5)" }}>
                    <Play className="w-9 h-9 text-[var(--t-cyan)] ml-1" />
                  </div>
                </div>
              )}

              {/* Bottom controls */}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 md:px-4 py-2 bg-gradient-to-t from-[#050912]/90 to-transparent">
                <button
                  onClick={toggleMute}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#050912]/80 border border-[var(--t-border-glow)] hover:border-[var(--t-blue)] transition text-[var(--t-blue-glow)] hover:text-[var(--t-cyan)]"
                  data-testid="showcase-mute-toggle"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase hidden sm:inline">{muted ? t("home.devFeedUnmute") : t("home.devFeedAudioOn")}</span>
                </button>
                <button
                  onClick={goFullscreen}
                  className="inline-flex items-center justify-center w-9 h-9 bg-[#050912]/80 border border-[var(--t-border-glow)] hover:border-[var(--t-blue)] transition text-[var(--t-blue-glow)] hover:text-[var(--t-cyan)]"
                  data-testid="showcase-fullscreen"
                  aria-label="Fullscreen"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DevStat({ label, value }) {
  return (
    <div className="border-l-2 border-[var(--t-blue)] pl-2.5">
      <div className="font-display text-base md:text-lg text-[var(--t-cyan)] leading-tight">{value}</div>
      <div className="font-mono text-[9px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase">{label}</div>
    </div>
  );
}
